/**
 * Плашка статуса.
 *
 * Текстовая подпись обязательна, цвет — дополнительный сигнал: интерфейс должен
 * читаться и без цвета (документ «Структура интерфейса», §2.6).
 */

import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import {
  getItemTypeDefinition,
  getStatusDefinition,
  type ItemType,
  type StatusTone,
} from '@project-os/contracts';
import { cn } from '@/lib/utils';

const statusBadgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center gap-1.5 rounded-md border px-1.5 py-0.5 text-2xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'border-tone-neutral-border bg-tone-neutral-bg text-tone-neutral',
        progress: 'border-tone-progress-border bg-tone-progress-bg text-tone-progress',
        success: 'border-tone-success-border bg-tone-success-bg text-tone-success',
        warning: 'border-tone-warning-border bg-tone-warning-bg text-tone-warning',
        danger: 'border-tone-danger-border bg-tone-danger-bg text-tone-danger',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface StatusBadgeProps
  extends ComponentProps<'span'>, VariantProps<typeof statusBadgeVariants> {
  label: string;
}

export function StatusBadge({ label, tone, className, ...props }: StatusBadgeProps) {
  return (
    <span className={cn(statusBadgeVariants({ tone }), className)} {...props}>
      {label}
    </span>
  );
}

/**
 * Статус элемента проекта. Подпись и тон берутся из реестра типов — в коде
 * интерфейса нет ни одного списка статусов.
 */
export function ItemStatusBadge({ type, status }: { type: ItemType; status: string }) {
  const definition = getStatusDefinition(type, status);
  return (
    <StatusBadge
      label={definition?.labelRu ?? status}
      tone={definition?.tone ?? 'neutral'}
      title={`${getItemTypeDefinition(type).labelRu}: ${definition?.labelRu ?? status}`}
    />
  );
}

export type { StatusTone };
